<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const { t } = useI18n();

const webhooks = computed(
  () => store.getters['prospectWebhooks/getProspectWebhooks']
);
const uiFlags = computed(() => store.getters['prospectWebhooks/getUIFlags']);

const showModal = ref(false);
const editingWebhook = ref(null);
const showDeleteConfirm = ref(false);
const deletingWebhook = ref(null);
const formName = ref('');
const formUrl = ref('');

const modalTitle = computed(() =>
  editingWebhook.value
    ? t('PROSPECT_WEBHOOKS.EDIT.TITLE')
    : t('PROSPECT_WEBHOOKS.ADD.TITLE')
);

const openAdd = () => {
  editingWebhook.value = null;
  formName.value = '';
  formUrl.value = '';
  showModal.value = true;
};

const openEdit = webhook => {
  editingWebhook.value = webhook;
  formName.value = webhook.name;
  formUrl.value = webhook.url;
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
};

const openDeleteConfirm = webhook => {
  deletingWebhook.value = webhook;
  showDeleteConfirm.value = true;
};

const closeDeleteConfirm = () => {
  showDeleteConfirm.value = false;
  deletingWebhook.value = null;
};

const handleSubmit = async () => {
  if (!formName.value || !formUrl.value) return;
  try {
    if (editingWebhook.value) {
      await store.dispatch('prospectWebhooks/update', {
        id: editingWebhook.value.id,
        name: formName.value,
        url: formUrl.value,
      });
      useAlert(t('PROSPECT_WEBHOOKS.EDIT.SUCCESS'));
    } else {
      await store.dispatch('prospectWebhooks/create', {
        name: formName.value,
        url: formUrl.value,
      });
      useAlert(t('PROSPECT_WEBHOOKS.ADD.SUCCESS'));
    }
    closeModal();
  } catch {
    useAlert(
      editingWebhook.value
        ? t('PROSPECT_WEBHOOKS.EDIT.ERROR')
        : t('PROSPECT_WEBHOOKS.ADD.ERROR')
    );
  }
};

const confirmDelete = async () => {
  try {
    await store.dispatch('prospectWebhooks/delete', deletingWebhook.value.id);
    useAlert(t('PROSPECT_WEBHOOKS.DELETE.SUCCESS'));
  } catch {
    useAlert(t('PROSPECT_WEBHOOKS.DELETE.ERROR'));
  } finally {
    closeDeleteConfirm();
  }
};

onMounted(() => {
  store.dispatch('prospectWebhooks/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.fetchingList"
    :loading-message="t('PROSPECT_WEBHOOKS.LOADING')"
    :no-records-message="t('PROSPECT_WEBHOOKS.EMPTY')"
    :no-records-found="!webhooks.length"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('PROSPECT_WEBHOOKS.HEADER')"
        :description="t('PROSPECT_WEBHOOKS.DESCRIPTION')"
      >
        <template #actions>
          <Button
            blue
            icon="i-lucide-circle-plus"
            :label="t('PROSPECT_WEBHOOKS.ADD_BUTTON')"
            @click="openAdd"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full divide-y divide-n-weak">
        <thead>
          <th
            v-for="header in [
              t('PROSPECT_WEBHOOKS.TABLE.NAME'),
              t('PROSPECT_WEBHOOKS.TABLE.URL'),
              t('PROSPECT_WEBHOOKS.TABLE.ACTIONS'),
            ]"
            :key="header"
            class="py-4 ltr:pr-4 rtl:pl-4 text-left font-semibold text-n-slate-11 last:text-right last:pr-4"
          >
            {{ header }}
          </th>
        </thead>
        <tbody class="divide-y divide-n-weak flex-1 text-n-slate-12">
          <tr v-for="webhook in webhooks" :key="webhook.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4 text-sm">{{ webhook.name }}</td>
            <td
              class="py-4 ltr:pr-4 rtl:pl-4 text-sm text-n-slate-11 truncate max-w-xs"
            >
              {{ webhook.url }}
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4 text-sm text-right">
              <div class="flex items-center justify-end gap-2">
                <Button
                  sm
                  ghost
                  slate
                  icon="i-lucide-pencil"
                  @click="openEdit(webhook)"
                />
                <Button
                  sm
                  ghost
                  slate
                  icon="i-lucide-trash-2"
                  @click="openDeleteConfirm(webhook)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-modal v-model:show="showModal" :on-close="closeModal">
      <div class="flex flex-col gap-4 px-6 pb-6 pt-4 w-[31.25rem]">
        <h2 class="text-lg font-semibold text-n-slate-12">{{ modalTitle }}</h2>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-11">
            {{ t('PROSPECT_WEBHOOKS.FORM.NAME.LABEL') }}
          </label>
          <input
            v-model="formName"
            type="text"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand"
            :placeholder="t('PROSPECT_WEBHOOKS.FORM.NAME.PLACEHOLDER')"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-11">
            {{ t('PROSPECT_WEBHOOKS.FORM.URL.LABEL') }}
          </label>
          <input
            v-model="formUrl"
            type="url"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand"
            :placeholder="t('PROSPECT_WEBHOOKS.FORM.URL.PLACEHOLDER')"
          />
        </div>
        <div class="flex justify-end gap-2 mt-2">
          <Button
            ghost
            slate
            :label="t('PROSPECT_WEBHOOKS.FORM.CANCEL')"
            @click="closeModal"
          />
          <Button
            blue
            :label="t('PROSPECT_WEBHOOKS.FORM.SUBMIT')"
            :disabled="!formName || !formUrl"
            :is-loading="uiFlags.creatingItem || uiFlags.updatingItem"
            @click="handleSubmit"
          />
        </div>
      </div>
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirm"
      :on-close="closeDeleteConfirm"
      :on-confirm="confirmDelete"
      :title="t('PROSPECT_WEBHOOKS.DELETE.CONFIRM_TITLE')"
      :message="
        t('PROSPECT_WEBHOOKS.DELETE.CONFIRM_MESSAGE', {
          name: deletingWebhook?.name,
        })
      "
      :confirm-text="t('PROSPECT_WEBHOOKS.DELETE.YES')"
      :reject-text="t('PROSPECT_WEBHOOKS.DELETE.NO')"
    />
  </SettingsLayout>
</template>
